/* eslint-disable consistent-return */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MediaMiniPlayer } from './MediaMiniPlayer';
import { SvgIcon } from 'common/react/components/SvgIcon';
import Countdown from 'react-countdown';
import { CountdownRenderer } from './CountdownRenderer';
import { getTimeString } from './util/getTimeString';
import { Sponsor } from './Sponsor';
import { ImageElement } from 'common/react/components/ImageElement';
import {
    MEDIATYPES,
    VERY_FREQUENT_REFETCH_INTERVAL,
    CLOSE_STREAM_END,
    SEMI_CLOSE_STREAM_END,
    FREQUENT_REFETCH_INTERVAL,
    LONG_REFETCH_INTERVAL
} from './constants';

export const TradeRadioMiniPlayer = ({
    event,
    config,
    brightcoveProps,
    onStreamEnded
}) => {
    const [streamEnded, setStreamEnded] = useState(false);
    const [countdownCompleted, setCountdownCompleted] = useState(false);
    const [hasTriggeredRefetch, setHasTriggeredRefetch] = useState(false);
    const styleObj = {
        '--trade-accent': config.accentColour,
        '--trade-accent-text': config.accentTextColour,
        '--trade-accent-hover': config.accentHoverColour,
        '--trade-bg': config.backgroundColour
    };

    const { LIVE } = PULSE.app.common.broadcast.status;

    const mediaType = PULSE.core.url.getParam('mediaType').toUpperCase();

    const getActiveChannel = () => {
        return event.channels.find((channel) => {
            const channelMediaType = channel?.info.channelTypes[0].mediaType;
            return channelMediaType?.toUpperCase() === mediaType;
        });
    };

    const activeChannel = getActiveChannel();

    // Create unique videoPlayerEleId for each stream to avoid conflicts
    const videoPlayerEleId = `trade-radio_${brightcoveProps.anchorName}_${
        event.id
    }_${
        activeChannel?.customAttributes?.find(
            (attr) => attr.name === 'brightcove_videoid_web'
        )?.value || 'default'
    }`;
    // Try and grab an image from the activeChannel
    const mediaImageUrl = activeChannel?.customAttributes.find(
        (attr) => attr.name === 'thumbnailURL'
    )?.value;

    const playerWidgetRef = useRef(null);

    // Check if we're past the active channel's endDateTime
    useEffect(() => {
        if (!activeChannel?.endDateTime) {
            return;
        }

        const checkEndTime = () => {
            const currentTime = new Date().getTime();
            const endTime = new Date(activeChannel.endDateTime).getTime();
            const timeUntilEnd = endTime - currentTime;

            if (currentTime > endTime) {
                setStreamEnded(true);
                // Don't trigger refetch immediately - wait for countdown to complete
                // This prevents immediate refetch when there might be a next event
                return;
            }

            // Determine check interval based on time remaining
            let nextInterval;
            if (timeUntilEnd <= CLOSE_STREAM_END) {
                // Last 5 minutes: check every second
                nextInterval = VERY_FREQUENT_REFETCH_INTERVAL;
            } else if (timeUntilEnd <= SEMI_CLOSE_STREAM_END) {
                // Last 30 minutes: check every 30 seconds
                nextInterval = FREQUENT_REFETCH_INTERVAL;
            } else {
                // More than 30 minutes: check every 5 minutes
                nextInterval = LONG_REFETCH_INTERVAL;
            }

            // Set up next check
            const timeout = setTimeout(checkEndTime, nextInterval);
            return timeout;
        };

        // Start the checking process
        const initialTimeout = checkEndTime();

        return () => {
            if (initialTimeout) {
                clearTimeout(initialTimeout);
            }
        };
    }, [activeChannel?.endDateTime, event.id, onStreamEnded]);

    // Reset streamEnded when a new event arrives (new event means new countdown should start)
    // But don't reset if it's the same event (happens when refetch returns null and we keep the old event)
    const [lastEventId, setLastEventId] = useState(null);

    useEffect(() => {
        if (event && activeChannel && event.id !== lastEventId) {
            setStreamEnded(false);
            setCountdownCompleted(false);
            setHasTriggeredRefetch(false); // Reset refetch flag for new event
            setLastEventId(event.id);
        }
    }, [event, activeChannel, lastEventId]);

    // Handle immediate refetch when stream ends
    useEffect(() => {
        if (streamEnded && !hasTriggeredRefetch && onStreamEnded) {
            // Trigger refetch immediately when stream ends
            setHasTriggeredRefetch(true);
            onStreamEnded();
        }
    }, [streamEnded, hasTriggeredRefetch, onStreamEnded]);

    const handleCountdownComplete = () => {
        setCountdownCompleted(true);

        // When countdown completes for a new event, trigger refetch for next event
        // Only if we're not in a streamEnded state (meaning this is a genuine new event countdown)
        if (!streamEnded && onStreamEnded) {
            onStreamEnded();
        }
    };

    return (
        <div
            className={`trade-radio-mini-player ${
                mediaType === MEDIATYPES.AUDIO
                    ? 'trade-radio-mini-player__audio'
                    : ''
            }`}
            ref={playerWidgetRef}
            style={styleObj}
        >
            <div className="trade-radio-mini-player__content">
                <div className="trade-radio-mini-player__header">
                    {config?.promo?.promoItem?.onDemandUrl ? (
                        <Sponsor promo={config.promo} isV2 />
                    ) : (
                        <h2 className="trade-radio-mini-player__heading">
                            {config.title}
                        </h2>
                    )}
                </div>
                {(event.status === LIVE && activeChannel && !streamEnded) ||
                (countdownCompleted && !streamEnded) ? (
                    <MediaMiniPlayer
                        key={`${event.id}-${
                            activeChannel?.customAttributes?.find(
                                (attr) => attr.name === 'brightcove_videoid_web'
                            )?.value
                        }`} // Force remount on stream change
                        featureChannel={activeChannel}
                        brightcoveProps={brightcoveProps}
                        videoPlayerEleId={videoPlayerEleId}
                        config={config}
                        title={event.name}
                        mediaType={mediaType}
                        onStreamEnded={() => setStreamEnded(true)}
                        playsInline
                    />
                ) : (
                    <ImageElement
                        url={mediaImageUrl}
                        cssClass="trade-radio-mini-player__image"
                    />
                )}
                <div className="trade-radio-mini-player__info">
                    {(event.status === LIVE && !streamEnded) ||
                    (countdownCompleted && !streamEnded) ? (
                        <>
                            {activeChannel.info.name && (
                                <h3 className="trade-radio-mini-player__title">
                                    {activeChannel.info.name}
                                </h3>
                            )}
                            {activeChannel.info.description && (
                                <p className="trade-radio-mini-player__description">
                                    {activeChannel.info.description}
                                </p>
                            )}
                        </>
                    ) : streamEnded ? (
                        <>
                            {config.eventFinishedLabel && (
                                <h2 className="trade-radio-mini-player__title">
                                    {config.eventFinishedLabel}
                                </h2>
                            )}
                        </>
                    ) : (
                        <>
                            <h2 className="trade-radio-mini-player__title">
                                {`${
                                    config.countdownSummaryLabel
                                } ${getTimeString(activeChannel)}`}
                            </h2>
                            <span className="trade-radio-mini-player__description">
                                {config.countdownTimerLabel}{' '}
                                <strong>
                                    <Countdown
                                        date={activeChannel.startDateTime}
                                        renderer={CountdownRenderer}
                                        onComplete={handleCountdownComplete}
                                    />
                                </strong>
                            </span>
                        </>
                    )}
                </div>

                <div className="trade-radio-mini-player__cta">
                    <a
                        className="trade-radio-mini-player__button"
                        href={config.linkTo}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                            /* eslint-disable camelcase */
                            PULSE.app.tracking.recordEvent('select_link', {
                                link_text: config.linkToLabel,
                                link_url: config.linkTo,
                                outbound: false,
                                type: 'internal',
                                component_name: `${
                                    event?.name ?? 'Trade Radio '
                                }`
                            });
                            /* eslint-enable camelcase */
                        }}
                    >
                        <span>{config.linkToLabel}</span>
                        <SvgIcon
                            icon="arrow-right"
                            className="trade-radio-mini-player__button--icon"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

TradeRadioMiniPlayer.propTypes = {
    config: PropTypes.object.isRequired,
    brightcoveProps: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    onStreamEnded: PropTypes.func
};
